import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

// Task 3.9 — the designed loading-state moment (DECISIONS.md D-27, "craft in the
// margins"). A brief reticle "lock-on" that plays once on the first cold paint of
// the app: the four corner brackets snap into place around the MockWit mark, then
// the overlay clears to reveal the site. It reuses the existing bracket "lock-on"
// language (BracketFrame / 3.6-3.7) as a one-time polish beat — NOT a per-route
// transition (route changes already cross-fade via View Transitions in App.js) and
// NOT a delay tactic: the whole thing runs well under ~600ms.
//
// Mounted once at the app shell (App.js), so it plays on a full page load and does
// not replay when navigating between routes (App does not remount).
//
// prefers-reduced-motion: skipped entirely — the site renders immediately with no
// overlay, matching the rest of the motion system's opt-out behaviour.

const TOTAL_MS = 650;

export default function LoadingReticle() {
  const reduced = usePrefersReducedMotion();
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (reduced) return undefined;
    const t = setTimeout(() => setDone(true), TOTAL_MS);
    return () => clearTimeout(t);
  }, [reduced]);

  if (reduced || done) return null;

  const corners = [
    { pos: 'top-0 left-0 border-l-2 border-t-2 rounded-tl-lg', from: { x: -14, y: -14 } },
    { pos: 'top-0 right-0 border-r-2 border-t-2 rounded-tr-lg', from: { x: 14, y: -14 } },
    { pos: 'bottom-0 left-0 border-l-2 border-b-2 rounded-bl-lg', from: { x: -14, y: 14 } },
    { pos: 'bottom-0 right-0 border-r-2 border-b-2 rounded-br-lg', from: { x: 14, y: 14 } },
  ];

  return (
    <motion.div
      aria-hidden="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-surface-base"
      initial={{ opacity: 1 }}
      animate={{ opacity: 0 }}
      transition={{ duration: 0.26, delay: 0.34, ease: 'easeIn' }}
    >
      <div className="relative w-28 h-28 sm:w-32 sm:h-32">
        {/* MockWit mark at the centre of the reticle */}
        <motion.span
          className="absolute inset-0 flex items-center justify-center font-mono font-extrabold text-3xl text-gradient-brand drop-shadow-glow"
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.05 }}
        >
          M
        </motion.span>
        {/* Corner brackets snap inward into their locked position */}
        {corners.map((c, i) => (
          <motion.span
            key={c.pos}
            className={`absolute w-8 h-8 border-brand-cyan ${c.pos}`}
            initial={{ opacity: 0, ...c.from }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ type: 'spring', stiffness: 360, damping: 24, delay: 0.04 * i }}
          />
        ))}
      </div>
    </motion.div>
  );
}
